import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import CodeSnippetBlock from "ds/components/CodeSnippetBlock";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { MAX_DURATION_IN_SECONDS } from "../FormDrawer/constants";
import {
  generateExampleTrustPolicyStatement,
  generateWorkerTrustPolicyStatement,
} from "./constants";
import { GET_SPACELIFT_AWS_ACCOUNT_ID } from "./gql";

type AwsIntegrationTrustPolicyExampleProps = {
  tagAssumeRole: boolean;
  showWarning: boolean;
  generateCredentialsInWorker: boolean;
  externalId: string;
  durationSeconds: number;
};

const AwsIntegrationTrustPolicyExample = ({
  tagAssumeRole,
  showWarning,
  generateCredentialsInWorker,
  externalId,
  durationSeconds,
}: AwsIntegrationTrustPolicyExampleProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const { data } = useQuery(GET_SPACELIFT_AWS_ACCOUNT_ID);

  const trimmedExternalId = externalId.trim();

  const parsedAssumeRolePolicyStatement = useMemo(() => {
    if (generateCredentialsInWorker) {
      return JSON.stringify(
        generateWorkerTrustPolicyStatement(trimmedExternalId, tagAssumeRole),
        null,
        2
      );
    }

    if (data?.spaceliftAwsAccountId) {
      return JSON.stringify(
        generateExampleTrustPolicyStatement(accountName, data.spaceliftAwsAccountId, tagAssumeRole),
        null,
        2
      );
    }

    return undefined;
  }, [accountName, data, tagAssumeRole, generateCredentialsInWorker, trimmedExternalId]);

  return (
    <Banner
      variant={showWarning ? "warning" : "info"}
      title="Configure the trust policy for the IAM role"
    >
      {showWarning && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          <b>
            {tagAssumeRole
              ? "The IAM role's trust policy must be updated in AWS to include the new TagSession statement."
              : "The IAM role's trust policy must be updated in AWS to remove the TagSession statement."}
          </b>
        </Typography>
      )}

      {generateCredentialsInWorker && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          Replace the principal with the IAM role your worker runs as (e.g. EC2 instance profile,
          IRSA or EKS Pod Identity). That role must be allowed to call sts:AssumeRole
          {tagAssumeRole && " and sts:TagSession"} on this role.
        </Typography>
      )}

      {generateCredentialsInWorker && durationSeconds > MAX_DURATION_IN_SECONDS && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          Make sure this role's maximum session duration in IAM is at least {durationSeconds}{" "}
          seconds, otherwise AWS rejects the role assumption.
        </Typography>
      )}

      {!generateCredentialsInWorker && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          Here's an example trust policy statement you can use, that allows any stack within your
          Spacelift account to use the IAM Role:
        </Typography>
      )}

      {parsedAssumeRolePolicyStatement && (
        <Box margin="small 0 0 0">
          <CodeSnippetBlock language="json" allowCopy>
            {parsedAssumeRolePolicyStatement}
          </CodeSnippetBlock>
        </Box>
      )}

      {generateCredentialsInWorker && !trimmedExternalId && (
        <Typography tag="p" variant="p-body3" margin="small 0 0 0">
          We recommend setting an external ID and restricting the trust policy to it.
        </Typography>
      )}
    </Banner>
  );
};

export default AwsIntegrationTrustPolicyExample;
